import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base, LockIcon, WarningCircleIcon } from '@itsrever/design-system'
import {
  GroupRouting,
  RoutingRule,
  Warehouse,
  defaultWarehouseId,
  inheritingStores,
  overridingStores,
  splitDefault
} from '@/domain/warehouses'
import {
  useResetStoreRules,
  useSaveGroupRules,
  useSaveStoreRules
} from '@/application/manage-warehouses'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { SaveBar } from '@/ui/shared'
import { equalJSON } from '@/utils'
import {
  DraftRule,
  RuleList,
  countChanges,
  countIncomplete,
  fromDraft,
  toDraft
} from './rule-list'
import { WarehousesScope } from '@/hooks/use-warehouses-scope'

const { Badge, Button, Callout, Card } = Base

type Scope = Exclude<WarehousesScope, { kind: 'none' }>

interface RoutingRulesProps {
  scope: Scope
  warehouses: Warehouse[]
  routing: GroupRouting
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}

export const RoutingRules: React.FC<RoutingRulesProps> = (props) =>
  props.scope.kind === 'group' ? (
    // Keyed by the stored rules so a save or a refetch replaces the draft it made obsolete.
    <GroupRules key={JSON.stringify(props.routing.rules)} {...props} />
  ) : (
    // Keyed so a store switch drops the previous store's draft and unlock.
    <StoreRules
      key={props.scope.ecommerceId}
      {...props}
      ecommerceId={props.scope.ecommerceId}
    />
  )

function useSummary(warehouses: Warehouse[]) {
  const { t } = useTranslation()
  return (rules: RoutingRule[]) => {
    const id = defaultWarehouseId(rules)
    const warehouse = warehouses.find((w) => w.id === id)?.company
    return warehouse
      ? t('warehouses.rules.summary', {
          count: splitDefault(rules).rules.length,
          warehouse
        })
      : t('warehouses.rules.summary_no_default', { count: rules.length })
  }
}

const GroupRules: React.FC<RoutingRulesProps> = ({
  scope,
  warehouses,
  routing,
  onShowStore
}) => {
  const { t } = useTranslation()
  const summary = useSummary(warehouses)
  const [draft, setDraft] = useState(() => toDraft(routing.rules))
  const { saveGroupRules, savingGroupRules } = useSaveGroupRules(scope.group)
  const { resetStoreRules } = useResetStoreRules(scope.group)
  const showDialog = useDialog()
  const inheriting = inheritingStores(routing, scope.stores).length
  const overriding = overridingStores(routing, scope.stores)
  const dirty = !equalJSON(fromDraft(draft), routing.rules)
  const incomplete = useIncomplete(draft)

  const resetStore = async (store: string) => {
    const confirmed = await showDialog({
      title: t('warehouses.rules.reset_confirm_title'),
      message: (
        <Trans
          i18nKey="warehouses.rules.reset_confirm_message"
          values={{ store, group: scope.group, count: inheriting }}
          components={{ b: <b /> }}
        />
      ),
      okButtonText: t('warehouses.rules.reset_confirm_ok'),
      cancelButtonText: t('warehouses.rules.cancel'),
      destructive: true
    })
    if (confirmed) resetStoreRules(store)
  }

  return (
    <Card variant="flat" className="flex flex-col gap-3 p-5">
      <Callout tone="success" className="text-xs leading-relaxed">
        <Trans
          i18nKey="warehouses.rules.group_banner"
          values={{ group: scope.group }}
          components={{ b: <b /> }}
        />
      </Callout>
      <RuleList
        rules={draft}
        saved={routing.rules}
        warehouses={warehouses}
        editable
        onChange={setDraft}
      />
      <div className="flex flex-col gap-1 border-t border-solid border-neutral-100 pt-3">
        <span className="text-sm font-medium text-neutral-900">
          {t('warehouses.rules.portals_title', { group: scope.group })}
        </span>
        {overriding.length ? (
          <span className="text-xs text-neutral-600">
            {t('warehouses.rules.portals_hint')}
          </span>
        ) : null}
      </div>
      <div className="rounded-lg border border-solid border-neutral-100">
        {scope.stores.map((store) => {
          const override = routing.overrides[store]
          return (
            <div
              key={store}
              className="grid grid-cols-[1fr_1fr_auto] items-center gap-3 border-b border-solid border-neutral-100 px-3 py-2 text-xs last:border-b-0"
            >
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="justify-self-start"
                onClick={() => onShowStore(store)}
              >
                {store}
              </Button>
              <span className="justify-self-start">
                {override ? (
                  <Badge tone="warning">
                    {t('warehouses.rules.own_rules', {
                      summary: summary(override)
                    })}
                  </Badge>
                ) : (
                  <Badge tone="success">
                    {t('warehouses.rules.follows_group')}
                  </Badge>
                )}
              </span>
              {override ? (
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t('warehouses.rules.edit_store_aria', {
                      store
                    })}
                    onClick={() => onShowStore(store)}
                  >
                    {t('warehouses.rules.edit_store')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t('warehouses.rules.reset_store_aria', {
                      store
                    })}
                    onClick={() => resetStore(store)}
                  >
                    {t('warehouses.rules.reset_to_group')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t('warehouses.rules.override_store_aria', {
                    store
                  })}
                  onClick={() => onShowStore(store)}
                >
                  {t('warehouses.rules.override_store')}
                </Button>
              )}
            </div>
          )
        })}
      </div>
      {dirty ? (
        <SaveBar
          changes={countChanges(draft, routing.rules)}
          problem={incomplete}
          saveLabel={t('warehouses.rules.save_for', { count: inheriting })}
          saving={savingGroupRules}
          onDiscard={() => setDraft(toDraft(routing.rules))}
          onSave={() => saveGroupRules(fromDraft(draft))}
        />
      ) : null}
    </Card>
  )
}

const StoreRules: React.FC<RoutingRulesProps & { ecommerceId: string }> = ({
  scope,
  ecommerceId,
  warehouses,
  routing,
  onShowGroup
}) => {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const override = routing.overrides[ecommerceId]
  const [unlocked, setUnlocked] = useState(false)
  const [draft, setDraft] = useState<DraftRule[]>(() =>
    toDraft(override ?? routing.rules)
  )
  const { saveStoreRules, savingStoreRules } = useSaveStoreRules(scope.group)
  const { resetStoreRules, resettingStoreRules } = useResetStoreRules(
    scope.group
  )
  const others = inheritingStores(routing, scope.stores).filter(
    (s) => s !== ecommerceId
  ).length
  const state = override ? 'overridden' : unlocked ? 'editing' : 'inherited'
  const incomplete = useIncomplete(draft)
  // An inherited store shows the group's rules as they are now, not as they were on mount.
  const rules = state === 'inherited' ? toDraft(routing.rules) : draft
  // Until it is saved, an override is measured against the group rules it starts from.
  const saved = override ?? routing.rules
  const groupLink = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      inline
      onClick={onShowGroup}
    />
  )
  const values = { store: ecommerceId, group: scope.group, count: others }
  const save = () => saveStoreRules({ ecommerceId, rules: fromDraft(draft) })

  const reset = async () => {
    const confirmed = await showDialog({
      title: t('warehouses.rules.reset_confirm_title'),
      message: (
        <Trans
          i18nKey="warehouses.rules.reset_confirm_message"
          values={values}
          components={{ b: <b /> }}
        />
      ),
      okButtonText: t('warehouses.rules.reset_confirm_ok'),
      cancelButtonText: t('warehouses.rules.cancel'),
      destructive: true
    })
    if (!confirmed) return
    resetStoreRules(ecommerceId, {
      onSuccess: () => {
        setUnlocked(false)
        setDraft(toDraft(routing.rules))
      }
    })
  }

  const pill = {
    inherited: (
      <Badge tone="info" icon={<LockIcon />}>
        {t('warehouses.rules.inherited_pill', { group: scope.group })}
      </Badge>
    ),
    editing: <Badge tone="warning">{t('warehouses.rules.editing_pill')}</Badge>,
    overridden: null
  }[state]

  return (
    <Card variant="flat" className="flex flex-col gap-3 p-5">
      {state === 'inherited' ? (
        <Callout
          tone="info"
          icon={<LockIcon />}
          className="text-xs leading-relaxed"
        >
          <Trans
            i18nKey="warehouses.rules.inherited_banner"
            values={values}
            components={{ b: <b />, action: groupLink }}
          />
        </Callout>
      ) : state === 'editing' ? (
        <Callout
          tone="warning"
          icon={<WarningCircleIcon />}
          className="text-xs leading-relaxed"
        >
          <Trans
            i18nKey="warehouses.rules.editing_banner"
            values={values}
            components={{ b: <b />, action: groupLink }}
          />
        </Callout>
      ) : (
        <Callout tone="warning" className="text-xs leading-relaxed">
          <Trans
            i18nKey="warehouses.rules.overridden_banner"
            values={values}
            components={{ b: <b />, action: groupLink }}
          />
        </Callout>
      )}
      <RuleList
        rules={rules}
        saved={saved}
        warehouses={warehouses}
        editable={state !== 'inherited'}
        status={pill}
        onChange={setDraft}
      />
      {state === 'inherited' ? (
        <Footer>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setUnlocked(true)}
          >
            <LockIcon className="size-3" />
            {t('warehouses.rules.unlock')}
          </Button>
        </Footer>
      ) : state === 'overridden' ? (
        <Footer>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={resettingStoreRules}
            onClick={reset}
          >
            {t('warehouses.rules.reset_store')}
          </Button>
        </Footer>
      ) : null}
      {state === 'editing' ? (
        // Unlocked but unsaved is a pending override even before any edit.
        <SaveBar
          pending
          changes={countChanges(draft, saved)}
          problem={incomplete}
          discardLabel={t('warehouses.rules.cancel')}
          saveLabel={t('warehouses.rules.save_store')}
          saving={savingStoreRules}
          onDiscard={() => {
            setUnlocked(false)
            setDraft(toDraft(routing.rules))
          }}
          onSave={save}
        />
      ) : state === 'overridden' && !equalJSON(fromDraft(draft), saved) ? (
        <SaveBar
          changes={countChanges(draft, saved)}
          problem={incomplete}
          saving={savingStoreRules}
          onDiscard={() => setDraft(toDraft(saved))}
          onSave={save}
        />
      ) : null}
    </Card>
  )
}

function useIncomplete(draft: DraftRule[]) {
  const { t } = useTranslation()
  const count = countIncomplete(draft)
  return count ? t('warehouses.rules.incomplete', { count }) : undefined
}

const Footer = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-end gap-2 border-t border-solid border-neutral-100 pt-3">
    {children}
  </div>
)
